import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { styled } from '@mui/material';
import {
    FormatBold,
    FormatItalic,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    Link as LinkIcon,
    HorizontalRule,
    Undo,
    Redo,
    Title,
} from '@mui/icons-material';
import { Colors } from 'library';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import { RIconButton } from './RIconButton';

export interface RRichTextEditorHandle {
    getHTML: () => string;
    getText: () => string;
    clear: () => void;
    isEmpty: () => boolean;
}

interface RRichTextEditorProps {
    placeholder?: string;
    onUpdate?: (state: { html: string; text: string; isEmpty: boolean }) => void;
    toolbarExtras?: React.ReactNode;
    minHeight?: number;
}

export const RRichTextEditor = forwardRef<RRichTextEditorHandle, RRichTextEditorProps>(function RRichTextEditor(
    { placeholder, onUpdate, toolbarExtras, minHeight = 120 },
    ref,
) {
    const editor = useEditor({
        extensions: [StarterKit, TiptapLink.configure({ openOnClick: false })],
        content: '',
        onUpdate: ({ editor: ed }) => {
            if (!onUpdate) return;
            const text = ed.getText();
            onUpdate({ html: ed.getHTML(), text, isEmpty: text.trim().length === 0 });
        },
    });

    useEffect(() => {
        return () => {
            editor?.destroy();
        };
    }, [editor]);

    useImperativeHandle(
        ref,
        () => ({
            getHTML: () => editor?.getHTML() ?? '',
            getText: () => editor?.getText() ?? '',
            clear: () => {
                editor?.commands.clearContent();
            },
            isEmpty: () => !editor || editor.getText().trim().length === 0,
        }),
        [editor],
    );

    const handleLink = () => {
        if (!editor) return;
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('URL', previousUrl || 'https://');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
    };

    if (!editor) return null;

    return (
        <Wrapper>
            <Toolbar>
                <RIconButton
                    icon={FormatBold}
                    size={28}
                    iconSize={18}
                    selected={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                />
                <RIconButton
                    icon={FormatItalic}
                    size={28}
                    iconSize={18}
                    selected={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                />
                <RIconButton
                    icon={Title}
                    size={28}
                    iconSize={18}
                    selected={editor.isActive('heading', { level: 2 })}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                />
                <RIconButton
                    icon={FormatListBulleted}
                    size={28}
                    iconSize={18}
                    selected={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                />
                <RIconButton
                    icon={FormatListNumbered}
                    size={28}
                    iconSize={18}
                    selected={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                />
                <RIconButton
                    icon={FormatQuote}
                    size={28}
                    iconSize={18}
                    selected={editor.isActive('blockquote')}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                />
                <RIconButton
                    icon={LinkIcon}
                    size={28}
                    iconSize={18}
                    selected={editor.isActive('link')}
                    onClick={handleLink}
                />
                <RIconButton
                    icon={HorizontalRule}
                    size={28}
                    iconSize={18}
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                />
                {toolbarExtras}
                <ToolbarSpacer />
                <RIconButton
                    icon={Undo}
                    size={28}
                    iconSize={18}
                    disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}
                />
                <RIconButton
                    icon={Redo}
                    size={28}
                    iconSize={18}
                    disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}
                />
            </Toolbar>
            <EditorArea minHeight={minHeight}>
                <EditorContent editor={editor} placeholder={placeholder} />
            </EditorArea>
        </Wrapper>
    );
});

const Wrapper = styled('div')({});

const Toolbar = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    padding: '6px 12px',
    borderBottom: `1px solid ${Colors.lightGrey}`,
    backgroundColor: Colors.white,
    flexWrap: 'wrap',
});

const ToolbarSpacer = styled('div')({
    flex: 1,
});

const EditorArea = styled('div')<{ minHeight: number }>(({ minHeight }) => ({
    padding: '12px 16px',
    minHeight,
    backgroundColor: Colors.faintGrey,
    '& .tiptap': {
        outline: 'none',
        fontSize: 14,
        lineHeight: 1.6,
        color: Colors.darkNavy,
        minHeight,
    },
    '& .tiptap p.is-editor-empty:first-child::before': {
        color: Colors.mediumGrey,
        content: 'attr(data-placeholder)',
        float: 'left',
        height: 0,
        pointerEvents: 'none',
    },
    '& .tiptap h2': {
        fontSize: 18,
        fontWeight: 600,
        margin: '8px 0 4px',
    },
    '& .tiptap blockquote': {
        borderLeft: `3px solid ${Colors.lightGrey}`,
        margin: '8px 0',
        paddingLeft: 12,
        color: Colors.mediumGrey,
    },
    '& .tiptap a': {
        color: '#2563eb',
        textDecoration: 'underline',
    },
    '& .tiptap ul, & .tiptap ol': {
        paddingLeft: 24,
    },
    '& .tiptap hr': {
        border: 'none',
        borderTop: `1px solid ${Colors.lightGrey}`,
        margin: '12px 0',
    },
}));
